import { ARTICLE_CATEGORY_LABELS, ARTICLE_STATUS_LABELS } from '@idpe/schema';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { ConfirmDialog } from '~/components/ui/Modal.js';
import { Spinner } from '~/components/ui/Spinner.js';
import { ArticleForm } from '~/features/articles/ArticleForm.js';
import { useArticle, useDeleteArticle } from '~/features/articles/hooks.js';
import { useCategoriesList } from '~/features/taxonomies/hooks.js';
import { ApiError } from '~/lib/api.js';

export const Route = createFileRoute('/_auth/articles/$slug')({
  component: ArticleEditPage,
});

function ArticleEditPage() {
  const { slug } = Route.useParams();
  const navigate = Route.useNavigate();
  const article = useArticle(slug);
  const categories = useCategoriesList();
  const del = useDeleteArticle();
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!article.data) return;
    try {
      await del.mutateAsync(article.data.id);
      toast.success('Artigo excluído.');
      setDeleting(false);
      navigate({ to: '/articles' });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao excluir.');
    }
  }

  if (article.isLoading || categories.isLoading) {
    return (
      <div className="page">
        <div className="dt__state">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (article.error || !article.data) {
    return (
      <div className="page">
        <header className="page-head">
          <p className="eyebrow">/ Erro</p>
          <h1 className="page-title">Artigo não encontrado.</h1>
        </header>
        <Link to="/articles">
          <Button variant="secondary">Voltar à lista</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Publicações</p>
        <h1 className="page-title">{article.data.titulo}</h1>
        <p className="page-sub" style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          <Badge tone="primary">{ARTICLE_CATEGORY_LABELS[article.data.categoriaSlug]}</Badge>
          <Badge
            tone={
              article.data.status === 'publicado'
                ? 'success'
                : article.data.status === 'arquivado'
                  ? 'muted'
                  : 'neutral'
            }
          >
            {ARTICLE_STATUS_LABELS[article.data.status]}
          </Badge>
          {article.data.destaque && <Badge tone="warning">destaque</Badge>}
          <span style={{ color: 'var(--fg-muted)' }}>· {article.data.tempoLeituraMin} min</span>
        </p>
      </header>

      {categories.data && <ArticleForm article={article.data} categories={categories.data} />}

      <section className="form-card form-card--danger">
        <h2 className="form-card__title">Ações sensíveis</h2>
        <p className="form-card__sub">
          A exclusão é permanente. Para tirar do ar sem perder o conteúdo, arquive o artigo (status
          → arquivado).
        </p>
        <div className="form-actions form-actions--gap">
          <Button variant="danger" onClick={() => setDeleting(true)}>
            Excluir artigo
          </Button>
        </div>
      </section>

      <Link to="/articles">
        <Button variant="ghost">← Voltar à lista</Button>
      </Link>

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={handleDelete}
        title="Excluir artigo?"
        description={`"${article.data.titulo}" será removido permanentemente.`}
        confirmLabel="Sim, excluir"
        variant="danger"
        loading={del.isPending}
      />
    </div>
  );
}
